import * as Sentry from '@sentry/react'

import env from '~/env'
import { urlToRegex } from '~/utils/observability'

import { groupHttpClientError } from './httpClientErrors'
import { tabAgeMs, withTabAge } from './tabAge'

export async function showUserFeedbackForm() {
    const client = Sentry.getClient<Sentry.BrowserClient>()
    const feedback = client?.getIntegrationByName<ReturnType<typeof Sentry.feedbackIntegration>>('Feedback')

    if (!feedback) {
        return null
    }

    // `useSentryUser` provides a mapping from the keys provided in `setUser()` for use in submitted feedback.
    const form = await feedback.createForm({ useSentryUser: { email: 'username', name: 'name' } })
    form.appendToDom()
    form.open()
}

if (env.VITE_SENTRY_DSN && env.VITE_ENABLE_SENTRY) {
    Sentry.setTags({
        'app.name': 'frontend',
        'app.git_tag': env.VITE_GIT_TAG,
        'app.git_sha': env.VITE_GIT_SHA,
        'tenant.name': env.VITE_TENANT_NAME,
    })
    const tracePropagationTargets: string[] = []

    if (env.VITE_ENABLE_SENTRY_TRACE_PROPAGATION_TO_API) {
        tracePropagationTargets.push(env.VITE_API_BASE_URL)
    }

    if (env.VITE_ENABLE_SENTRY_TRACE_PROPAGATION_TO_DIPS) {
        tracePropagationTargets.push(env.VITE_DIPS_BASE_URL)
    }

    const integrations = [Sentry.browserTracingIntegration(), Sentry.httpClientIntegration(), Sentry.openFeatureIntegration()]

    if (env.VITE_ENABLE_SENTRY_USER_FEEDBACK) {
        integrations.push(
            Sentry.feedbackIntegration({
                enableScreenshot: false, // Keep this disabled to avoid leaking sensitive data.
                autoInject: false,
                showBranding: false,
                showName: false,
                showEmail: false,
                formTitle: 'Gi tilbakemelding',
                submitButtonLabel: 'Send inn',
                cancelButtonLabel: 'Avbryt',
                confirmButtonLabel: 'Bekreft',
                isRequiredLabel: '',
                messageLabel: 'Husk å ikke dele sensitiv informasjon her.',
                messagePlaceholder: 'Gikk noe galt, i såfall hva da? Hva forventet du at skulle skje?',
                successMessageText: 'Takk for din tilbakemelding!',
            })
        )
    }

    if (env.VITE_SENTRY_ENVIRONMENT === 'local') {
        integrations.push(Sentry.captureConsoleIntegration({ levels: ['log', 'info', 'warn', 'error', 'debug', 'assert'] }))
    } else {
        integrations.push(Sentry.captureConsoleIntegration({ levels: ['warn', 'error', 'assert'] }))
    }

    Sentry.init({
        dsn: env.VITE_SENTRY_DSN,
        environment: env.VITE_SENTRY_ENVIRONMENT,
        release: env.VITE_GIT_TAG,
        ignoreErrors: [
            // This happens when user has the app open in a background tab.
            // The socket reconnects correctly once the tab is brought back to the foreground.
            /^Heartbeat timed out, closing connection/,
        ],
        integrations,
        beforeSend: event => withTabAge(groupHttpClientError(event), tabAgeMs()),
        // Performance Monitoring
        tracesSampleRate: Number(env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 1.0),
        // Session Replay
        // replaysSessionSampleRate: The sample rate for replays that begin recording immediately and last the entirety of the user's session.
        // You may want to change this to 100% while in development and then sample at a lower rate in production.
        replaysSessionSampleRate: Number(env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE ?? 0.1),
        // replaysOnErrorSampleRate: the sample rate for replays that are recorded when an error happens. This type of replay will record
        // up to a minute of events prior to the error and continue recording until the session ends.
        // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        replaysOnErrorSampleRate: Number(env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE ?? 1.0),
        // tracePropagationTargets attaches baggage and sentry-trace http headers to the listed urls
        tracePropagationTargets: tracePropagationTargets.map(urlToRegex),
    })
} else {
    console.warn('Sentry disabled')
}
